import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import '../shared/i18n';
import { ColorModeProvider } from '../shared/theme/ColorModeContext';
import { AuthProvider } from '../shared/hooks/useAuth';
import { AuthGuard } from '../shared/guards/AuthGuard';
import { AppShell } from '../shared/components/layout/AppShell';
import { PublicShell } from '../shared/components/layout/PublicShell';
import { NotificationProvider } from '../shared/notifications/NotificationProvider';

import { LoginPage } from '../modules/auth/LoginPage';
import { RegisterPage } from '../modules/auth/RegisterPage';
import { ForgotPasswordPage } from '../modules/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../modules/auth/ResetPasswordPage';
import { VerifyEmailPage } from '../modules/auth/VerifyEmailPage';
import { VerifyPhonePage } from '../modules/auth/VerifyPhonePage';
import { OAuthCallbackPage } from '../modules/auth/OAuthCallbackPage';

import { HomePage } from '../modules/home/HomePage';
import { SearchPage } from '../modules/search/SearchPage';
import { CategoryLandingPage } from '../modules/categories/CategoryLandingPage';
import { ProviderPage } from '../modules/providers/ProviderPage';
import { BookingPage } from '../modules/providers/BookingPage';
import { BookingSubmittedPage } from '../modules/providers/BookingSubmittedPage';
import { GalleryPage } from '../modules/providers/GalleryPage';
import { ProLandingPage } from '../modules/pro/ProLandingPage';
import { ProRegisterPage } from '../modules/pro/ProRegisterPage';
import { ProLoginPage } from '../modules/pro/ProLoginPage';
import { LegalNoticePage } from '../modules/legal/LegalNoticePage';
import { PrivacyPolicyPage } from '../modules/legal/PrivacyPolicyPage';
import { AccountDeletionPage } from '../modules/legal/AccountDeletionPage';
import { TermsPage } from '../modules/legal/TermsPage';
import { ContactPage } from '../modules/contact/ContactPage';
import { CustomerBookingsPage } from '../modules/bookings/CustomerBookingsPage';
import { ProviderBookingsPage } from '../modules/bookings/ProviderBookingsPage';
import { InboxPage } from '../modules/chat/InboxPage';
import { ConversationPage } from '../modules/chat/ConversationPage';
import { OpportunitiesFeedPage } from '../modules/opportunities/OpportunitiesFeedPage';
import { OpportunityDetailPage } from '../modules/opportunities/OpportunityDetailPage';
import { CreateOpportunityPage } from '../modules/opportunities/CreateOpportunityPage';
import { EditOpportunityPage } from '../modules/opportunities/EditOpportunityPage';
import { SavedOpportunitiesPage } from '../modules/opportunities/SavedOpportunitiesPage';
import { ProviderCalendarPage } from '../modules/availability/ProviderCalendarPage';
import { BecomeProviderPage } from '../modules/providers/BecomeProviderPage';
import { DashboardPage } from '../modules/dashboard/DashboardPage';
import { AdminProvidersPage } from '../modules/admin/AdminProvidersPage';
import { AdminProviderPortfolioPage } from '../modules/admin/AdminProviderPortfolioPage';
import { AdminProviderReviewPage } from '../modules/admin/AdminProviderReviewPage';
import { AdminUsersPage } from '../modules/admin/AdminUsersPage';
import { AccountSettingsPage } from '../modules/account/AccountSettingsPage';
import { AdminInsightsPage } from '../modules/admin/AdminInsightsPage';
import { NotificationsPage } from '../modules/notifications/NotificationsPage';
import { RequestsListPage } from '../modules/requests/RequestsListPage';
import { RequestDetailPage } from '../modules/requests/RequestDetailPage';
import { CreateRequestPage } from '../modules/requests/CreateRequestPage';
import { MyRequestsPage } from '../modules/requests/MyRequestsPage';

import { NotFoundPage, ForbiddenPage } from '../shared/components/ui/ErrorPages';
import { RootErrorBoundary } from '../shared/components/ui/RootErrorBoundary';
import { InstallPwaPrompt } from '../shared/components/ui/InstallPwaPrompt';
import { FloatingWhatsAppButton } from '../shared/components/ui/CommunityCta';
import { GoogleAnalytics } from '../shared/analytics/GoogleAnalytics';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
});

export default function App() {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ColorModeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <NotificationProvider>
              <AuthProvider>
                <BrowserRouter>
                {/* GA4 gtag.js bootstrap + SPA page_view tracking. No-op
                    when VITE_GA_MEASUREMENT_ID is unset, so dev sessions
                    never hit Google. Must live inside BrowserRouter so
                    useLocation() resolves. */}
                <GoogleAnalytics />
                <Routes>
                  {/* Standalone auth pages (no shell) */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route
                    path="/verify-email"
                    element={
                      <AuthGuard skipVerificationGate>
                        <VerifyEmailPage />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/verify-phone"
                    element={
                      <AuthGuard skipVerificationGate>
                        <VerifyPhonePage />
                      </AuthGuard>
                    }
                  />
                  {/* OAuth landing — receives tokens in the query string from
                      the backend callback, scrubs them, hydrates the session,
                      then navigates to ?next= (or /dashboard). */}
                  <Route path="/auth/callback" element={<OAuthCallbackPage />} />
                  <Route path="/forbidden" element={<ForbiddenPage />} />
                  <Route path="/pro" element={<ProLandingPage />} />
                  <Route path="/pro/login" element={<ProLoginPage />} />
                  <Route path="/pro/register" element={<ProRegisterPage />} />

                  {/* Public marketplace (anyone can browse) */}
                  <Route element={<PublicShell />}>
                    <Route index element={<HomePage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/category/:slug" element={<CategoryLandingPage />} />
                    <Route path="/providers/:id" element={<ProviderPage />} />
                    <Route path="/providers/:id/gallery" element={<GalleryPage />} />
                    <Route path="/providers/:id/book" element={<BookingPage />} />
                    {/* Confirmation page shown right after a booking submit.
                        State (provider name, requested date) is passed via
                        navigate(..., { state }) — direct URL access bounces
                        to /bookings. */}
                    <Route path="/booking-submitted" element={<BookingSubmittedPage />} />
                    <Route path="/legal" element={<LegalNoticePage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/account-deletion" element={<AccountDeletionPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    {/* Community "I'm looking for…" requests — public feed
                        and detail page (creation requires auth, handled by
                        the page itself). */}
                    <Route path="/requests" element={<RequestsListPage />} />
                    {/* Creation is public — the form prompts for a signup
                        at submit time (so a visitor can describe what they
                        need before being asked to register). */}
                    <Route path="/requests/new" element={<CreateRequestPage />} />
                    <Route path="/requests/:id" element={<RequestDetailPage />} />
                    {/* Opportunities community feed — phase 1 is read-only;
                        anonymous visitors are welcome (SEO + viral sharing).
                        Phase 2 adds /new and /:postId/edit — auth is enforced
                        inside each page (bounce to /login?next=…). */}
                    <Route path="/opportunities" element={<OpportunitiesFeedPage />} />
                    <Route path="/opportunities/new" element={<CreateOpportunityPage />} />
                    <Route path="/opportunities/saved" element={<SavedOpportunitiesPage />} />
                    <Route
                      path="/opportunities/:postId/edit"
                      element={<EditOpportunityPage />}
                    />
                    <Route path="/opportunities/:postId" element={<OpportunityDetailPage />} />
                  </Route>

                  {/* Authenticated area */}
                  <Route
                    element={
                      <AuthGuard>
                        <AppShell />
                      </AuthGuard>
                    }
                  >
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/dashboard/bookings" element={<ProviderBookingsPage />} />
                    <Route path="/dashboard/availability" element={<ProviderCalendarPage />} />
                    <Route path="/bookings" element={<CustomerBookingsPage />} />
                    <Route path="/messages" element={<InboxPage />} />
                    <Route path="/messages/:conversationId" element={<ConversationPage />} />
                    <Route path="/my-requests" element={<MyRequestsPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/become-provider" element={<BecomeProviderPage />} />
                    <Route path="/account" element={<AccountSettingsPage />} />
                    <Route path="/admin/providers" element={<AdminProvidersPage />} />
                    <Route
                      path="/admin/providers/:id"
                      element={<AdminProviderReviewPage />}
                    />
                    <Route
                      path="/admin/providers/:id/portfolio"
                      element={<AdminProviderPortfolioPage />}
                    />
                    <Route path="/admin/users" element={<AdminUsersPage />} />
                    <Route path="/admin/insights" element={<AdminInsightsPage />} />
                  </Route>

                  <Route path="*" element={<NotFoundPage />} />
                  <Route path="/home-redirect" element={<Navigate to="/" replace />} />
                  </Routes>
                  {/* Global PWA install prompt — fixed-position banner,
                      sits above every route. Self-hides when the app is
                      already running standalone, after install, or when
                      the visitor dismisses it. */}
                  <InstallPwaPrompt />
                  {/* Floating WhatsApp circle — always-on community
                      entry point at bottom-right (zIndex 1200, sits
                      under the PWA install banner at 1300 during the
                      brief window both are visible). Hides itself when
                      VITE_COMMUNITY_WHATSAPP_URL is unset. */}
                  <FloatingWhatsAppButton />
                </BrowserRouter>
              </AuthProvider>
            </NotificationProvider>
          </LocalizationProvider>
        </ColorModeProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
